import {useEffect, useState} from 'react';

import {searchAccountProductSubscriptions} from 'src/openapi/admin/apis/AccountProductSubscriptionsApi';
import {ProductSubscriptionLifecycleStatus} from 'src/openapi/admin/models/ProductSubscriptionLifecycleStatus';
import {useAppDispatch} from 'src/store/hooks';

import type {Error} from 'src/openapi/admin/models/Error';
import type {ProductSubscription} from 'src/openapi/admin/models/ProductSubscription';

const PAGE_SIZE = 100;
const AVAILABLE_STATUSES = [
    ProductSubscriptionLifecycleStatus.ACTIVE,
    ProductSubscriptionLifecycleStatus.PENDING,
];

export const useAccountProductAccessTerms = (accountId: number) => {
    const apiDispatch = useAppDispatch();
    const [terms, setTerms] = useState<ProductSubscription[] | null>(null);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const abortController = new AbortController();
        setTerms(null);
        setError(null);

        const loadTerms = async () => {
            try {
                const loadedTerms: ProductSubscription[] = [];
                let page = 1;
                let totalCount = 0;

                do {
                    const response = await apiDispatch(searchAccountProductSubscriptions({
                        accountId,
                        productSubscriptionSearchRequest: {
                            pagination: {page, pageSize: PAGE_SIZE},
                            filters: {lifecycleStatuses: AVAILABLE_STATUSES},
                        },
                    }, {signal: abortController.signal}));

                    if (abortController.signal.aborted) {
                        return;
                    }
                    if (!response.payload?.success) {
                        throw response.payload?.data;
                    }

                    const result = response.payload.data;
                    loadedTerms.push(...(result?.content || []));
                    totalCount = result?.totalCount || loadedTerms.length;
                    page += 1;
                } while (loadedTerms.length < totalCount);

                if (!abortController.signal.aborted) {
                    setTerms(loadedTerms);
                }
            } catch (requestError) {
                if (!abortController.signal.aborted) {
                    setTerms([]);
                    setError(requestError as Error);
                }
            }
        };

        loadTerms();
        return () => abortController.abort();
    }, [accountId, apiDispatch]);

    return {terms, error};
};
